import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import ReactTable from 'react-table'
import moment from 'moment'
import { has, isEqual } from 'lodash'
import 'react-table/react-table.css'

import { startDateFormatString } from '../../js/constants'
import { SHIP_TYPES_TEXT } from '../terminalVisitForm/shipselector/ShipSelector'
import { createVisitOrderPairs } from '../../utils/terminalVisitUtils'
import { getProductUnit, getAllProductsAtQuays } from '../../utils/general'
import './ShipVisitList.scss'

export default class ShipVisitList extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      showStatusFilter: false,
      showShipTypeFilter: false,
      filteredTerminalVisits: [],
      allProducts: getAllProductsAtQuays(this.props.quays)
    }

    // Used to get currently shown terminal visits
    this.tableInstance = null
    this.selectedCheckboxes = new Set(props.statuses)
    // this.selectedCheckboxes.delete('sailed')
    this.selectedCheckboxesShipType = new Set(Object.keys(SHIP_TYPES_TEXT))
    this.oldProps = { ...props }
  }

  componentDidUpdate(previousProps) {
    if (this.props.quays && !isEqual(this.props.quays, previousProps.quays)) {
      this.setState({
        allProducts: getAllProductsAtQuays(this.props.quays)
      })
    }
  }

  static propTypes = {
    terminalVisits: PropTypes.array,
    onSelectVisit: PropTypes.func.isRequired,
    selectedVisit: PropTypes.object,
    selectedVisits: PropTypes.array.isRequired,
    statuses: PropTypes.array.isRequired,
    getFilteredTerminalVisits: PropTypes.bool.isRequired,
    onChangeFilter: PropTypes.func,
    scrollBarWidth: PropTypes.number,
    conflicts: PropTypes.object.isRequired,
    quays: PropTypes.array.isRequired,
    selectedDateRange: PropTypes.object,
    hoveredShip: PropTypes.func
  }

  toggleCheckbox = label => {
    if (this.selectedCheckboxes.has(label)) {
      this.selectedCheckboxes.delete(label)
    } else {
      this.selectedCheckboxes.add(label)
    }
  }

  toggleCheckboxShipType = label => {
    if (this.selectedCheckboxesShipType.has(label)) {
      this.selectedCheckboxesShipType.delete(label)
    } else {
      this.selectedCheckboxesShipType.add(label)
    }
  }

  render() {
    const columns = [
      {
        id: 'startTime',
        Header: 'Start date',
        accessor: item => item.order.cargoStart,
        Cell: props => moment(props.value).format(startDateFormatString),
        filterable: false,
        width: 125
      },
      {
        id: 'cargo',
        Header: 'Cargo',
        accessor: item => item.order.product,
        Cell: props => {
          return (
            <div>
              <span
                className={`fa ${
                  props.original.order.operation === 'unloading' ? 'fa-arrow-circle-up' : 'fa-arrow-circle-down'
                } cargo-type-indicator`}
                style={{ color: props.original.order.color ? props.original.order.color : '#000' }}
              />
              {props.value}
            </div>
          )
        }
      },
      {
        id: 'amount',
        Header: 'Amount',
        accessor: item => item.order.amount,
        Cell: props => props.value + ' ' + getProductUnit(this.state.allProducts, props.original.order.product),
        filterable: false,
        width: 65
      },
      {
        id: 'destination',
        Header: 'Destination',
        accessor: 'order.destination',
        filterable: true,
        width: 75
      },
      {
        id: 'type',
        Header: 'Type',
        accessor: 'visit.ship.type',
        width: 55,
        Cell: props => {
          const shipDetails = props.original.visit.ship ? props.original.visit.ship : { length: 'UNKNOWN' }
          const isSeaShip = shipDetails.type !== 'barge' // 'sea-ship' or 'coaster'

          return (
            <div>
              <span>{isSeaShip ? <i className="fa fa-anchor sea-ship-indicator" aria-hidden="true" /> : null}</span>
            </div>
          )
        },
        filterMethod: (filter, row) => this.selectedCheckboxesShipType.has(row[filter.id]),
        // eslint-disable-next-line react/prop-types
        Filter: ({ onChange }) => {
          return (
            <div className="status-filter">
              <div className="filter-toggler" onClick={this.handleToggleShipTypeFilter}>
                {this.state.showShipTypeFilter ? 'Hide' : 'Show'}{' '}
                <span className="amount-selected">
                  ({this.selectedCheckboxesShipType.size}/{Object.keys(SHIP_TYPES_TEXT).length})
                </span>
              </div>

              <div className={this.state.showShipTypeFilter ? 'show' : 'hidden'}>
                {Object.keys(SHIP_TYPES_TEXT).map(type => {
                  const isChecked = this.selectedCheckboxesShipType.has(type)

                  return (
                    <div key={type} className="checkbox">
                      <label>
                        <input
                          type="checkbox"
                          name={'type'}
                          checked={isChecked}
                          onChange={() => {
                            this.toggleCheckboxShipType(type)
                            onChange(type)
                          }}
                        />
                        <div className="checkbox-label">{shipTypeShortName(type)}</div>
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        }
      },
      {
        id: 'ship',
        Header: 'Ship',
        accessor: item => item.visit.ship,
        sortable: false,
        Cell: props => {
          const shipDetails = props.original.visit.ship ? props.original.visit.ship : { length: 'UNKNOWN' }
          return (
            <div>
              <span>{shipDetails.name ? shipDetails.name : shipDetails.length + ' m'}</span>
            </div>
          )
        },
        filterMethod: (filter, row) =>
          (typeof row[filter.id].name === 'string' &&
            row[filter.id].name.toLowerCase().includes(filter.value.toLowerCase())) ||
          (typeof row[filter.id].length === 'number' &&
            (row[filter.id].length.toString() + ' m').toLowerCase().startsWith(filter.value.toLowerCase()))
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: item => item.visit.status,
        width: 80,
        Cell: props => props.value.charAt(0).toUpperCase() + props.value.slice(1),
        sortMethod: (a, b) => {
          return this.props.statuses.indexOf(a) - this.props.statuses.indexOf(b)
        },
        filterMethod: (filter, row) => this.selectedCheckboxes.has(row[filter.id]),
        // eslint-disable-next-line react/prop-types
        Filter: ({ onChange }) => {
          return (
            <div className="status-filter">
              <div className="filter-toggler" onClick={this.handleToggleStatusFilter}>
                {this.state.showStatusFilter ? 'Hide' : 'Show'} filter{' '}
                <span className="amount-selected">
                  ({this.selectedCheckboxes.size}/{this.props.statuses.length})
                </span>
              </div>

              <div className={this.state.showStatusFilter ? 'show' : 'hidden'}>
                {this.props.statuses.map(status => {
                  const isChecked = this.selectedCheckboxes.has(status)

                  return (
                    <div key={status} className="checkbox">
                      <label>
                        <input
                          type="checkbox"
                          value={status}
                          name={'status'}
                          checked={isChecked}
                          onChange={event => {
                            this.toggleCheckbox(event.target.value)
                            onChange(event.target.value)
                          }}
                        />
                        <div className="checkbox-label">{status}</div>
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        }
      }
    ]

    const tdProps = (state, row) => {
      let isSelectedItem = this.props.selectedVisit && row && row.original.visit._id === this.props.selectedVisit._id

      if (!isSelectedItem && this.props.selectedVisits.length) {
        isSelectedItem = this.props.selectedVisits.find(tv => row && row.original.visit._id === tv._id)
      }

      return {
        onClick: () => {
          if (row) {
            if (
              this.props.selectedVisits.length &&
              row.original.visit.ship.mmsi === this.props.selectedVisits[0].ship.mmsi
            ) {
              this.props.onSelectVisit(null, null)
            } else {
              this.props.onSelectVisit(row.original.visit, row.original.order.uuid)
            }
          }
        },
        style: {
          background: `${isSelectedItem ? '#cfe0e6' : ''}`
        },
        onMouseEnter: () => {
          if (row && row.original.visit.ship && this.props.hoveredShip) {
            this.props.hoveredShip(row.original.visit.ship.mmsi)
          }
        },
        onMouseLeave: () => {
          if (row && this.props.hoveredShip) {
            this.props.hoveredShip('')
          }
        }
      }
    }

    const trProps = (state, rowInfo) => {
      const hasConflicts = rowInfo
        ? has(this.props.conflicts, [rowInfo.original.visit._id, rowInfo.original.order.uuid])
        : false
      const terminalVisitConflicts = hasConflicts ? { background: '#ffbab3' } : null

      return {
        style: terminalVisitConflicts
      }
    }

    const tHeadProps = () => {
      return {
        style: {
          paddingRight: this.props.scrollBarWidth ? this.props.scrollBarWidth : 0
        }
      }
    }

    const prettyScrollbarClass = this.props.scrollBarWidth ? 'prettyscrollbarfix' : ''

    // create order+visit pairs
    const items = createVisitOrderPairs(this.props.terminalVisits)

    const filteredItems = this.props.selectedDateRange
      ? items.filter(
          item =>
            item.order.cargoStart > moment(this.props.selectedDateRange.from).subtract(1, 'day').valueOf() &&
            item.order.cargoStart < moment(this.props.selectedDateRange.to).add(1, 'week').valueOf()
        )
      : items

    return (
      <ReactTable
        ref={el => (this.tableInstance = el)}
        data={filteredItems}
        columns={columns}
        getTdProps={tdProps}
        getTrProps={trProps}
        filterable
        defaultFilterMethod={this.filterMethod}
        defaultPageSize={18}
        showPageSizeOptions={false}
        showPageJump={false}
        showPagination={
          this.state.filteredTerminalVisits.length && this.state.filteredTerminalVisits.length < 18 ? false : true
        }
        className={'-striped -highlight ' + prettyScrollbarClass}
        onFilteredChange={this.handleFilteredChange}
        getTheadProps={tHeadProps}
        getTheadFilterProps={tHeadProps}
        defaultFiltered={[
          {
            id: 'status',
            value: ''
          }
        ]}
      />
    )
  }

  filterMethod = (filter, row) => {
    const id = filter.pivotId || filter.id
    if (!row[id]) {
      return false
    }
    return typeof row[id] === 'string' ? row[id].toLowerCase().includes(filter.value.toLowerCase()) : true
  }

  handleFilteredChange = () => {
    this.setState({ filteredTerminalVisits: this.tableInstance.state.sortedData })
    if (this.props.onChangeFilter) {
      this.props.onChangeFilter(this.tableInstance.state.sortedData)
    }
  }

  handleToggleStatusFilter = () => {
    this.setState({ showStatusFilter: !this.state.showStatusFilter })
  }

  handleToggleShipTypeFilter = () => {
    this.setState({ showShipTypeFilter: !this.state.showShipTypeFilter })
  }
}

function shipTypeShortName(type) {
  return type === 'sea-ship' ? 'sea' : type
}
